/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useKnowledgeBase } from 'components/knowledgeBase/knowledgeBaseContext';
import { SearchBar } from 'components/knowledgeBase/SearchBar';
import { DirectoryResults, Results, TagResults } from 'components/knowledgeBase/Results';
import { Breadcrumbs } from 'components/knowledgeBase/Breadcrumbs';
import { classNames } from 'primereact/utils';
import { RecommendedArticles } from 'components/knowledgeBase/RecommendedArticles';
import { ProgramArticles } from 'components/knowledgeBase/ProgramArticles';
import { DirectoryCards } from 'components/knowledgeBase/DirectoryCards';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import { useProgramPanel } from 'contexts/programPanelContext';
import { Button } from 'components/_new/Button';
import { Divider } from 'components/_new/Divider';
import { ProgramKnowledgeBaseArticle } from './ProgramKnowledgeBaseArticle';
import { TaggedArticles } from '../../knowledgeBase/TaggedArticles';
import { ProgramKnowledgeBaseArticleAdd } from './ProgramKnowledgeBaseArticleAdd';
import { ProgramKnowledgeBaseArticleEdit } from './ProgramKnowledgeBaseArticleEdit';
import { ModifyDirectoryDialog } from './components/ModifyDirectoryDialog';

const ProgramKnowledgeBaseBrowser = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { data: program } = useProgramPanel();
	const { knowledgeBaseLinkPrefix, isArticlePage, isMainPage, isSearch, isDirectory, currentDirectory, article } =
		useKnowledgeBase();

	const [addDirectoryModalOpen, setAddDirectoryModalOpen] = useState(false);

	return (
		<>
			<ModifyDirectoryDialog
				directoryId={undefined}
				visible={addDirectoryModalOpen}
				onHide={() => {
					setAddDirectoryModalOpen(false);
				}}
			/>
			<Helmet title={t('organizationPanel.knowledgeFiles')} />
			<SubPageTitle
				title={t('organizationPanel.knowledgeFiles')}
				additional={program?.displayName}
				right={() => (
					<>
						{isMainPage && !isArticlePage && (
							<div className="flex gap-2">
								<Button
									label={t('knowledgeBase.programPanel.addNewMaterial')}
									onClick={() => navigate({ pathname: 'add' })}
									variant="primary"
									icon="plus"
								/>
								<Button
									label={t('knowledgeBase.programPanel.addNewDirectory')}
									onClick={() => setAddDirectoryModalOpen(true)}
									variant="primary-outlined"
								/>
							</div>
						)}
						{!isMainPage && !isArticlePage && (
							<Button
								label={t('knowledgeBase.programPanel.addNewArticle')}
								onClick={() =>
									navigate({
										pathname: `${knowledgeBaseLinkPrefix}/add`,
										search: currentDirectory
											? new URLSearchParams({
													directory: String(currentDirectory?.id),
											  }).toString()
											: undefined,
									})
								}
							/>
						)}
						{isArticlePage && article && (
							<Button
								label={t('knowledgeBase.programPanel.editArticle')}
								onClick={() =>
									navigate({
										pathname: `${knowledgeBaseLinkPrefix}/edit/${article.articleId}`,
									})
								}
								disabled={article.info.isSystem}
							/>
						)}
					</>
				)}
			/>

			<div
				className={classNames('flex flex-column gap-2', {
					'pb-3': !isArticlePage,
					'h-full overflow-y-hidden': isArticlePage || isDirectory,
				})}
			>
				<Breadcrumbs />
				<div
					id="knowledgeBaseContainer"
					className={classNames('flex flex-column gap-4 ', {
						'flex-1 h-full overflow-y-scroll': isArticlePage || isDirectory,
					})}
				>
					<Routes>
						<Route
							index
							element={
								<>
									<SearchBar />
									{isMainPage && !isSearch && (
										<>
											<RecommendedArticles
												renderWrapper={(hasArticles, children) =>
													hasArticles ? (
														<>
															<div className="flex flex-column gap-1">
																<h3 className="text-base">
																	{t('knowledgeBase.homepage.recommendedArticles')}
																</h3>
																{children}
															</div>
															<Divider />
														</>
													) : null
												}
											/>

											<ProgramArticles
												renderWrapper={(hasArticles, children) =>
													hasArticles ? (
														<>
															<div className="flex flex-column gap-1">{children}</div>
															<Divider />
														</>
													) : null
												}
											/>

											<div className="flex flex-column gap-1">
												<h3 className="text-base">
													{t('knowledgeBase.homepage.techniquesByApplication')}
												</h3>
												<TaggedArticles />
											</div>

											<Divider />

											<div className="flex flex-column gap-1">
												<h3 className="text-base">
													{t('knowledgeBase.homepage.allMaterialsByCategory')}
												</h3>
												<DirectoryCards />
											</div>
										</>
									)}
									<Results />
								</>
							}
						/>
						<Route
							path="directory/*"
							element={
								<>
									<SearchBar />
									<DirectoryResults />
								</>
							}
						/>
						<Route
							path="tag/*"
							element={
								<>
									<SearchBar />
									<TagResults />
								</>
							}
						/>
						<Route path="article/*" element={<ProgramKnowledgeBaseArticle disableRating />} />
					</Routes>
				</div>
			</div>
		</>
	);
};

export const ProgramKnowledgeBasePage = () => {
	return (
		<Routes>
			<Route path="add" element={<ProgramKnowledgeBaseArticleAdd />} />
			<Route path="edit/:articleId" element={<ProgramKnowledgeBaseArticleEdit />} />
			<Route path="*" element={<ProgramKnowledgeBaseBrowser />} />
		</Routes>
	);
};
